import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OFFER_SOURCE, OFFER_STATUS, OFFER_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@frontend/shadcn/components/ui/drawer';
import { cn } from '@frontend/shadcn/lib/utils';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SALES_APP_ID } from '@frontend/app/constants/applicationIds';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import {
  useApolloClient,
  useGetOffersForSelectionQuery,
  useUpdateOfferMutation,
} from '@frontend/applications/AffiliatesApp/hooks';
import { filter, isEmpty, isNull, map, orderBy } from 'lodash';
import { format } from 'date-fns';
import { logger } from '@common';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { RefreshOfferDefaultImage } from '@frontend/applications/AffiliatesApp/components/OfferImage/RefreshOfferDefaultImage';
import { Offer } from './types';

// Types
interface OfferConnectDrawerProps {
  projectId: number;
  offerType: OFFER_TYPE;
  isOpen: boolean;
  onClose: () => void;
  offerConnectMode: 'connect' | 'update';
}

// Components
const LoadingComponent = () => (
  <div className="flex flex-col gap-2 p-4">
    <Skeleton className="w-full h-[50px]" />
    <Skeleton className="w-full h-[50px]" />
    <Skeleton className="w-full h-[50px]" />
  </div>
);

const OfferNameCell = ({ row }: { row: { original: Offer } }) => (
  <div className="flex items-center gap-1">
    <RefreshOfferDefaultImage
      imageUrl={row.original.nameData.imageUrl}
      source={row.original.nameData.source === OFFER_TYPE.PROMO_CODE ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE}
      icon={row.original.nameData.iconName}
      iconClassName="h-3 w-3"
      className="w-8 h-8"
    />
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="font-medium">
            {row.original.nameData.name?.length > 15
              ? `${row.original.nameData.name.slice(0, 15)}...`
              : row.original.nameData.name}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {row.original.nameData.name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);

const StatusCell = ({ row }: { row: { original: Offer } }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case OFFER_STATUS.ACTIVE:
        return 'bg-green-100 text-green-700';
      case OFFER_STATUS.PAUSED:
        return 'bg-yellow-100 text-yellow-700';
      case OFFER_STATUS.ARCHIVED:
        return 'bg-gray-100 text-gray-700';
      case OFFER_STATUS.DELETED:
        return 'bg-red-100 text-red-700';
      case 'EXPIRED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Badge
      variant="default"
      className={cn(
        'font-normal px-3 py-1 rounded',
        getStatusColor(row.original.expired ? 'EXPIRED' : row.original.status),
      )}
    >
      {row.original.expired ? 'EXPIRED' : row.original.status }
    </Badge>
  );
};

// Main Component
export const OfferConnectDrawer = (props: OfferConnectDrawerProps) => {
  const { projectId, isOpen, onClose, offerType, offerConnectMode } = props;
  const location = useLocation();
  const history = useHistory();
  const staApolloClient = useApolloClient();
  const [editOffer] = useUpdateOfferMutation();
  const [selectedId, setSelectedId] = React.useState<number | null>(null);

  const { data: offersData, loading: offersLoading } = useGetOffersForSelectionQuery({
    fetchPolicy: 'no-cache',
    variables: {
      query: {
        type: offerType,
      },
    },
    client: staApolloClient,
  });

  const tableData = useMemo(() => {
    if (offersData?.offers) {
      const filteredOffers = filter(offersData.offers, (o) => o.programId !== projectId);
      const sortedOffers = orderBy(filteredOffers, 'createdDate', 'desc');

      return map(sortedOffers, (offer) => {
        const offerDetails = isEmpty(offer.promos) ? offer.links[0] : offer.promos[0];
        return {
          id: offer.id,
          nameData: {
            source: offerType,
            name: offer.name,
            imageUrl: offer.imageUrl,
            iconName: offer.icon
          },
          creationDate: format(new Date(offer.createdDate), 'MM/dd/yyyy'),
          status: isNull(offer.archivedDate) ? offerDetails.status : 'Archive',
          expired: offer.expired,
          programId: offer.programId,
          isOfferArchived: !isNull(offer.archivedDate),
        };
      });
    }
    return [];
  }, [offersData, projectId, offerType]);

  const handleApply = async () => {
    try {
      await editOffer({
        variables: {
          id: selectedId,
          data: { programId: projectId },
        },
        client: staApolloClient,
      });
    } catch (e) {
      logger.error(e);
    } finally {
      onClose();
    }
  };

  const onCreateNewOffer = () => {
    const creationPage = offerType === OFFER_TYPE.PROMO_CODE ? 'newShopifyPromo' : 'newAffiliateLink';
    history.push({
      ...location,
      pathname: `/app/${SALES_APP_ID}/offers/${creationPage}`,
      search: new URLSearchParams({
        program_id: projectId.toString(),
      }).toString(),
    });
  };

  const columns = React.useMemo<ColumnDef<Offer, unknown>[]>(
    () => [
      {
        id: 'checkbox',
        header: '',
        size: 36,
        minSize: 36,
        maxSize: 36,
        cell: ({ row }) => {
          const offer = row.original;
          const isDeactivatedOffer = offer.status !== OFFER_STATUS.ACTIVE;
          const isDisabled = !!row.original.programId || isDeactivatedOffer;
          const tooltipMessage = row.original.programId
            ? 'This offer is already connected to a program'
            : isDeactivatedOffer
              ? 'This offer is not active'
              : '';

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Checkbox
                      checked={selectedId === row.original.id}
                      onCheckedChange={(checked) => {
                        setSelectedId(checked ? row.original.id : null);
                      }}
                      aria-label={`Select ${row.original.nameData.name}`}
                      className="mx-auto"
                      disabled={isDisabled}
                    />
                  </div>
                </TooltipTrigger>
                {isDisabled && (
                  <TooltipContent>
                    {tooltipMessage}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        },
        enableResizing: false,
      },
      {
        accessorKey: 'nameData',
        header: 'Offer Name',
        cell: OfferNameCell,
        size: 220,
      },
      {
        accessorKey: 'creationDate',
        header: 'Creation Date',
        cell: ({ row }) => {
          const date = new Date(row.original.creationDate);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        },
        size: 140,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: StatusCell,
        size: 100,
      },
    ],
    [selectedId],
  );

  return (
    <Drawer open={isOpen} onOpenChange={onClose} direction="right">
      <DrawerContent
        className={cn(
          'fixed left-auto inset-y-0 mt-0 right-0 z-[9999] flex h-full w-[576px] flex-col border-l bg-background rounded-none p-0',
          'duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'bg-white',
          'top-[var(--header-height,0px)] h-[calc(100vh-var(--header-height,0px))]',
        )}
        data-vaul-no-drag
      >
        <DrawerHeader className="flex justify-between items-center border-b border-grey-3 px-6 py-4">
          <DrawerTitle className="font-medium mb-0">{offerConnectMode === 'connect' ? 'Connect Offer' : 'Update Offer'}</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="headerIcon"
              className="border border-transparent hover:border-grey-6 hover:bg-transparent"
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        {offersLoading ? (
          <LoadingComponent />
        ) : (
          <div className="flex flex-col gap-3 p-4 overflow-y-scroll">
            <div className="flex items-center justify-between">
              <p className="text-sm font-regular m-0">Select 1 offer to connect to this campaign.</p>
              <Button onClick={onCreateNewOffer} className="flex items-center gap-2" variant="outline">
                <FontAwesomeIcon icon={faPlus} />
                Create Offer
              </Button>
            </div>
            <DataTable<Offer, unknown> columns={columns} data={tableData} className="mt-2" />
          </div>
        )}
        <DrawerFooter className="flex flex-row gap-2 space-x-2 border-t border-grey-3">
          <Button onClick={handleApply} disabled={!selectedId}>
            {offerConnectMode === 'connect' ? 'Connect Offer' : 'Update Offer'}
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
